import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { CheckCircleOutlined } from '@ant-design/icons';

import {
  Col,
  Row,
  VertTypography,
  VertImage,
  VertSteps,
  VertIcon,
} from '@vertem-core/vertice-ds';

import usePipeContext from 'Services/Providers/PipeProvider/usePipeContext';

import 'Assets/styles/default/components/Orders/OrderDetails/OrderDetailsStyles.less';

import { formatDateAndTime, formatDayMonthYear } from 'Utils/commons/date';
import { getOrderStatus } from 'Utils/orders';
import { limitString } from 'Utils/commons/string';
import {
  normalizeOrderTracking,
  getTrackingCurrentStep,
  getTrackingStatus,
} from 'Utils/orders';
import { pointsMask } from 'Utils/commons/points';
import generateUUID from 'Utils/generateUUID';
import { spotTrackingImageDefault } from 'Constants/Constants';
import ynToBool from 'Utils/ynToBool';

const OrderItemTracking = ({
  item,
  orderDate,
  orderNumber,
  status,
  vendorName,
  orderId,
  vendorOrderId,
  vendorId,
  vendorShippingValue,
}) => {
  const { callApi } = usePipeContext();
  const tracking = useSelector(({ Tracking: data }) => data);
  const Catalog = useSelector(({ Catalog: data }) => data);

  const {
    currencyConfiguration: { decimalPlaces, pointsName },
  } = Catalog;
  const notHasTracking =
    tracking && tracking.error && tracking.error.response.status === 404;

  const statusColor = getOrderStatus(status).color;
  const statusLabel = getOrderStatus(status).label;
  const normalizedTracking =
    tracking && tracking.statuses && normalizeOrderTracking(tracking.statuses);
  const currentTrackingStep = getTrackingCurrentStep(normalizedTracking);
  const currentTrackingStatus = getTrackingStatus(
    currentTrackingStep,
    normalizedTracking
  );
  const isShippingFree = vendorShippingValue === 0;
  const isPgsProductCode = item.productCode.includes('PGS');

  const formattedShipping = pointsMask(
    vendorShippingValue,
    decimalPlaces,
    pointsName
  );

  const loadTracking = (vendorOrderId, vendorId, productCode) => {
    const aliasUUID = generateUUID();

    callApi({
      symbol: `FETCH_TRACKING`,
      alias: aliasUUID,
      payload: {
        vendorOrderId,
        vendorId,
        productCode,
      },
    });
  };

  const loadTrackingPGS = (vendorOrderId, productCode) => {
    const aliasUUID = generateUUID();

    callApi({
      symbol: `FETCH_TRACKING_PGS`,
      alias: aliasUUID,
      payload: {
        vendorOrderId,
        productCode,
      },
    });
  };

  useEffect(() => {
    if (isPgsProductCode) {
      return loadTrackingPGS(vendorOrderId, item.productCode);
    }

    return loadTracking(vendorOrderId, vendorId, item.productCode);
  }, []);

  return (
    <Col className="order-tracking-content col">
      <Row className="tracking-number-and-status" align="middle" gutter={6}>
        <VertTypography.Paragraph className="order-number">
          #Pedido {orderNumber}
        </VertTypography.Paragraph>
        <VertTypography.Paragraph className={`order-status ${statusColor}`}>
          <Col className="dot-status" /> {statusLabel}
        </VertTypography.Paragraph>
      </Row>

      <Row className="tracking-infos" align="middle" gutter={6}>
        <VertImage
          rootClassName="item-image"
          alt={item.name}
          src={item.imageUrl || spotTrackingImageDefault}
        />
        <Col className="col">
          <VertTypography.Paragraph className="item-name">
            {limitString(item.name)}
          </VertTypography.Paragraph>
          <VertTypography.Paragraph className="item-vendor">
            Fornecido e entregue por {vendorName}
          </VertTypography.Paragraph>
          {isPgsProductCode &&
            (tracking.urlTracking || tracking.trackingCode) && (
              <VertTypography.Paragraph className="item-code">
                Código de rastreio:{' '}
                {tracking.urlTracking ? (
                  <a href={tracking.urlTracking} target="_blank">
                    {tracking.trackingCode || tracking.urlTracking}
                  </a>
                ) : (
                  <span>{tracking.trackingCode}</span>
                )}
              </VertTypography.Paragraph>
            )}
          <VertTypography.Paragraph className="item-shipping-value"
              style={{
                display: ynToBool(process.env.REACT_APP_HIDE_FREIGHT) ? 'none' : ''
              }}
          
          >
            Frete:{' '}
            {isShippingFree ? (
              <span className="highlight">Grátis</span>
            ) : (
              <span>
                {formattedShipping.value} {formattedShipping.label}
              </span>
            )}
          </VertTypography.Paragraph>
        </Col>
      </Row>

      {!!tracking.deliveryForecast && (
        <Row className="tracking-delivery-forecast" align="middle" gutter={6}>
          <VertTypography.Paragraph>
            Previsão de entrega: {formatDayMonthYear(tracking.deliveryForecast)}
          </VertTypography.Paragraph>
        </Row>
      )}

      <Row className="tracking-details" align="middle" gutter={6}>
        <Col className="col padding-x-none">
          <VertTypography.Paragraph>
            Detalhes do rastreio
          </VertTypography.Paragraph>
          {notHasTracking && (
            <Col className="tracking-item-not-supported col padding-x-none">
              <VertTypography.Paragraph className="status-title">
                <VertIcon component={CheckCircleOutlined} />
                Produto solicitado
              </VertTypography.Paragraph>
              <VertTypography.Paragraph className="status-description">
                {formatDateAndTime(orderDate)}
              </VertTypography.Paragraph>
            </Col>
          )}
          {!notHasTracking && (
            <VertSteps
              size="small"
              items={normalizedTracking}
              initial={0}
              current={currentTrackingStep}
              direction="vertical"
              status={currentTrackingStatus}
            />
          )}
        </Col>
      </Row>
    </Col>
  );
};
OrderItemTracking.propTypes = {
  item: PropTypes.object.isRequired,
  orderDate: PropTypes.string.isRequired,
  orderNumber: PropTypes.number.isRequired,
  status: PropTypes.string.isRequired,
  vendorName: PropTypes.string.isRequired,
  vendorOrderId: PropTypes.number.isRequired,
  vendorId: PropTypes.number.isRequired,
  vendorShippingValue: PropTypes.number.isRequired,
};

export default OrderItemTracking;
